import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AvatarIcon, CountryFlag } from "@/lib/identity";
import { Crown, ChatCircle, Trophy, Fire, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * ChampionshipLive — public spectator watch page for Day-31 championship.
 *
 *   ┌ Bracket (10 → 8 → 6 → 4 → 1) ────────────────┐
 *   │ live-updated via WS `snapshot` / `round_ended`│
 *   ├ Current question (spectator view, no submit) ─┤
 *   ├ Country Cup scoreboard (GH vs NG bar) ───────┤
 *   ├ Reactions row + chat sidebar ─────────────────┤
 *   └───────────────────────────────────────────────┘
 */
const REACTION_EMOJIS = ["🔥", "🎉", "💀", "👏", "⚡", "❤️", "😂", "🇬🇭", "🇳🇬", "🏆"];

export default function ChampionshipLive() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [status, setStatus] = useState(null);
  const [lobby, setLobby] = useState(null);   // {scheduled_at, accepted[], pending_count}
  const [currentQ, setCurrentQ] = useState(null);
  const [roundIntro, setRoundIntro] = useState(null);  // {round, theme, description, countdown}
  const [welcome, setWelcome] = useState(null);        // {rules, countdown} — 60s pre-tournament briefing
  const [floating, setFloating] = useState([]);
  const [flashes, setFlashes] = useState([]);
  const [myPick, setMyPick] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const wsRef = useRef(null);

  const iAmContender = status?.role === "contender";
  const iAmQualifier = status?.role === "qualifier";
  const me = status?.contender || null;

  useEffect(() => { setMyPick(null); }, [currentQ?.qid]);

  // Refresh personal status (contender/qualifier/spectator)
  const refreshStatus = () => {
    api.get("/championship/my-status")
      .then((r) => setStatus(r.data))
      .catch(() => {});
    api.get("/championship/lobby")
      .then((r) => setLobby(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    const token = localStorage.getItem("nk_session_token");
    if (!token) return;
    let closedByUnmount = false;
    let retryDelay = 1000;
    let retryTimer = null;

    const hydrate = () => {
      api.get("/championship/state").then((r) => {
        setState(r.data);
        if (r.data.championship?.current_question) setCurrentQ(r.data.championship.current_question);
      }).catch(() => {});
      refreshStatus();
    };

    const applyStandings = (standings) => {
      // Live-merge total_score + round_scores back into c.contenders
      // so the bracket + Country Cup update instantly, without a full re-fetch.
      if (!Array.isArray(standings) || standings.length === 0) return;
      setState((prev) => {
        if (!prev?.championship) return prev;
        const byId = Object.fromEntries(standings.map((s) => [s.user_id, s]));
        const nextContenders = (prev.championship.contenders || []).map((x) => {
          const s = byId[x.user_id];
          return s ? { ...x, total_score: s.total_score, round_scores: s.round_scores } : x;
        });
        return { ...prev, championship: { ...prev.championship, contenders: nextContenders } };
      });
    };

    const connect = () => {
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
      const ws = new WebSocket(`${base}/api/championship/ws?token=${token}`);
      wsRef.current = ws;
      ws.onopen = () => { retryDelay = 1000; };  // reset backoff on success
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === "snapshot") {
            setState(msg.data);
            if (msg.data.championship?.current_question) {
              setCurrentQ(msg.data.championship.current_question);
            }
          }
          if (msg.type === "championship_welcome") {
            setWelcome({ ...msg.data, countdown: msg.data.seconds });
            setRoundIntro(null);
            setCurrentQ(null);
          }
          if (msg.type === "championship_reset") {
            setWelcome(null);
            setRoundIntro(null);
            setCurrentQ(null);
            setState((prev) => ({ ...prev, championship: null }));
            refreshStatus();
          }
          if (msg.type === "round_started") {
            setWelcome(null);
            setCurrentQ(null);
            setRoundIntro({ ...msg.data, countdown: msg.data.intro_seconds || 15 });
          }
          if (msg.type === "question_started") {
            setRoundIntro(null);
            setCurrentQ(msg.data);
          }
          if (msg.type === "question_ended") {
            setCurrentQ((prev) => prev ? { ...prev, ended: true, correct_index: msg.data.correct_index } : prev);
            applyStandings(msg.data.standings);
          }
          if (msg.type === "round_ended") setCurrentQ(null);
          if (msg.type === "championship_ended") { setCurrentQ(null); setRoundIntro(null); }
          if (msg.type === "reaction") {
            const id = Math.random().toString(36).slice(2);
            const x = 10 + Math.random() * 80;
            setFloating((prev) => [...prev, { id, emoji: msg.data.emoji, x }]);
            setTimeout(() => setFloating((prev) => prev.filter((f) => f.id !== id)), 3000);
          }
          if (msg.type === "country_cup_resolved" || msg.type === "round_ended" || msg.type === "championship_started") {
            hydrate();
          }
        } catch { /* ignore */ }
      };
      ws.onerror = () => {};
      ws.onclose = () => {
        if (closedByUnmount) return;
        // Exponential backoff up to 10s + resync state via HTTP
        retryTimer = setTimeout(() => {
          retryDelay = Math.min(retryDelay * 2, 10000);
          hydrate();
          connect();
        }, retryDelay);
      };
    };

    connect();
    hydrate();

    // On tab focus, resync via HTTP — cheap safety net against missed frames
    const onVisibility = () => { if (!document.hidden) hydrate(); };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      closedByUnmount = true;
      document.removeEventListener("visibilitychange", onVisibility);
      if (retryTimer) clearTimeout(retryTimer);
      try { wsRef.current?.close(); } catch { /* noop */ }
    };
  }, []);

  // Round intro countdown
  useEffect(() => {
    if (!roundIntro) return;
    if (roundIntro.countdown <= 0) return;
    const t = setTimeout(() => {
      setRoundIntro((prev) => prev ? { ...prev, countdown: prev.countdown - 1 } : null);
    }, 1000);
    return () => clearTimeout(t);
  }, [roundIntro]);

  // Welcome countdown (60s pre-tournament rules)
  useEffect(() => {
    if (!welcome) return;
    if (welcome.countdown <= 0) return;
    const t = setTimeout(() => {
      setWelcome((prev) => prev ? { ...prev, countdown: prev.countdown - 1 } : null);
    }, 1000);
    return () => clearTimeout(t);
  }, [welcome]);

  const send = async (emoji) => {
    try {
      await api.post("/championship/reactions", { emoji });
    } catch (e) {
      if (e.response?.status === 429) toast.error("Slow down 🙂");
    }
  };

  const submitAnswer = async (optionIndex) => {
    if (myPick != null || submitting || !currentQ || currentQ.ended) return;
    setMyPick(optionIndex);
    setSubmitting(true);
    try {
      await api.post("/championship/answer", {
        round: currentQ.round,
        question_index: currentQ.index,
        qid: currentQ.qid,
        option_index: optionIndex,
      });
    } catch (e) {
      const msg = e.response?.data?.detail || "Answer failed";
      if (e.response?.status !== 409) {  // 409 = already answered — silent
        toast.error(msg);
        setMyPick(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const c = state?.championship;
  if (!c) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center" data-testid="champ-live-idle">
        <Crown size={40} weight="fill" className="mx-auto text-nk-gold mb-3" />
        <h1 className="font-display text-2xl mb-2">No championship live right now</h1>
        <p className="text-sm text-nk-mute">The next Continental Championship kicks off on Day 31 of the current season.</p>
      </div>
    );
  }

  const allContenders = c.contenders || [];
  // Country Cup lives on the live sum of each contender's total_score
  // (server also maintains a c.country_cup dict, but we compute locally so
  // late-joiner / live-answer updates reflect instantly)
  const cupGH = allContenders.filter(x => x.country === "GH").reduce((a,x) => a + (x.total_score||0), 0);
  const cupNG = allContenders.filter(x => x.country === "NG").reduce((a,x) => a + (x.total_score||0), 0);
  const cupTotal = (cupGH + cupNG) || 1;
  const ghPct = Math.round((cupGH / cupTotal) * 100);
  const cupWinner = c.country_cup?.winner;

  return (
    <div className="max-w-4xl mx-auto space-y-6 relative" data-testid="champ-live">
      {/* Floating reactions layer */}
      <div className="pointer-events-none fixed inset-0 z-40 overflow-hidden">
        {floating.map((f) => (
          <span
            key={f.id}
            className="absolute text-4xl animate-float-up"
            style={{ left: `${f.x}%`, bottom: 60 }}
          >
            {f.emoji}
          </span>
        ))}
      </div>

      <header>
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <p className="eyebrow text-nk-bad mb-1">
              {c.state === "RUNNING" ? "● LIVE" : c.state === "READY" ? "Starting soon" : "Ended"}
            </p>
            <h1 className="font-display text-3xl font-medium tracking-tight">
              Continental Championship · Season {c.season_number}
            </h1>
          </div>
          {c.paused && (
            <span className="text-xs px-2 py-0.5 border border-nk-warn/60 text-nk-warn rounded-sm">
              Paused by admin
            </span>
          )}
        </div>
      </header>

      {/* Bracket strip — every contender is always active, no elimination */}
      <div className="nk-card p-4" data-testid="champ-bracket">
        <p className="eyebrow mb-3">
          Round {c.current_round || 0} of {c.total_rounds}
          {c.current_round_theme && <span className="text-nk-gold"> · {c.current_round_theme}</span>}
        </p>
        <div className="flex items-center gap-1.5 flex-wrap">
          {allContenders.map((p) => (
            <BracketPill key={p.user_id} p={p} state="alive"
              flash={flashes.find((f) => f.user_id === p.user_id)?.kind} />
          ))}
        </div>
      </div>

      {/* Country cup — live sum of contender scores per country */}
      {allContenders.length > 0 && (
        <div className="nk-card p-4" data-testid="champ-country-cup">
          <div className="flex items-center justify-between mb-2 text-xs">
            <span className="flex items-center gap-2">
              <CountryFlag country="GH" size={12} /> Ghana <span className="font-mono text-nk-text">{cupGH}</span>
            </span>
            <span className="text-nk-mute">Country Cup</span>
            <span className="flex items-center gap-2">
              <span className="font-mono text-nk-text">{cupNG}</span> Nigeria <CountryFlag country="NG" size={12} />
            </span>
          </div>
          <div className="h-2 bg-nk-line rounded-full overflow-hidden flex">
            <div className="bg-red-500" style={{ width: `${ghPct}%` }} />
            <div className="bg-green-600 flex-1" />
          </div>
          {cupWinner && (
            <p className="mt-2 text-xs text-nk-gold">
              🏆 {cupWinner === "GH" ? "Ghana" : "Nigeria"} won the Country Cup!
              NK+ granted: {c.country_cup?.granted?.qualified || 0} qualifiers, {c.country_cup?.granted?.citizens || 0} citizens.
            </p>
          )}
        </div>
      )}

      {/* Pre-start lobby — shown when champ exists + scheduled but not yet running */}
      {!welcome && !roundIntro && !currentQ && lobby && lobby.state !== "RUNNING" && lobby.scheduled_at && (
        <PreStartLobby lobby={lobby} status={status} />
      )}

      {/* Welcome/rules card — 60s briefing at champ start */}
      {welcome && (
        <div className="nk-card p-6 border-nk-gold/60 bg-gradient-to-br from-nk-gold/[0.07] to-transparent"
             data-testid="championship-welcome">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-mono uppercase tracking-widest text-nk-gold mb-2 flex items-center gap-1.5">
                <Trophy size={12} weight="fill" /> WELCOME TO THE ARENA
              </p>
              <h2 className="font-display text-3xl mb-1">{welcome.title}</h2>
              <p className="text-sm text-nk-mute">{welcome.subtitle}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-mono uppercase text-nk-dim">Arena opens in</p>
              <p className="font-mono text-4xl text-nk-gold">{welcome.countdown}s</p>
            </div>
          </div>
          <div className="mt-5">
            <p className="text-[10px] font-mono uppercase text-nk-gold mb-2">Rules</p>
            <ul className="space-y-2">
              {(welcome.rules || []).map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-nk-text">
                  <Check size={14} className="text-nk-gold flex-shrink-0 mt-0.5" weight="bold" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Animated per-country contender reveal — staggered flip-in */}
          <ContenderReveal contenders={c.contenders || []} />
          {iAmContender && (
            <div className="mt-5 p-3 rounded-sm border border-nk-gold/40 bg-nk-charcoal/50">
              <p className="text-sm text-nk-gold">
                You&apos;re a contender — take a breath, then bring it.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Round intro card — 15s countdown before each round begins */}
      {roundIntro && (
        <div className="nk-card p-6 border-nk-gold/50 bg-nk-gold/[0.04]" data-testid="round-intro">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-mono uppercase tracking-widest text-nk-gold flex items-center gap-1.5 mb-2">
                {roundIntro.sudden_death
                  ? <><Fire size={12} weight="fill" /> GRAND FINAL · SUDDEN DEATH</>
                  : roundIntro.country_cup_round
                    ? <><Trophy size={12} weight="fill" /> COUNTRY CUP DECIDER</>
                    : <>ROUND {roundIntro.round} OF {roundIntro.total_rounds}</>}
              </p>
              <h2 className="font-display text-3xl mb-2">{roundIntro.theme}</h2>
              <p className="text-sm text-nk-mute leading-relaxed max-w-2xl">{roundIntro.description}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-mono uppercase text-nk-dim">Starts in</p>
              <p className="font-mono text-4xl text-nk-gold">{roundIntro.countdown}s</p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
            <IntroBadge Icon={Check} label="Questions" value={roundIntro.questions} />
            <IntroBadge Icon={ChatCircle} label="Per question" value={`${roundIntro.question_seconds}s`} />
            <IntroBadge Icon={Trophy} label="Scoring" value="+25 fastest / +10 correct" />
          </div>
          {iAmContender && (
            <div className="mt-4 p-3 rounded-sm border border-nk-gold/40 bg-nk-charcoal/50">
              <p className="text-sm">
                <span className="text-nk-gold font-medium">You&apos;re playing this round.</span>{" "}
                <span className="text-nk-mute">Get ready — first question lands in a few seconds.</span>
              </p>
            </div>
          )}
        </div>
      )}

      {/* Qualifier banner — shown to top-5 GH/NG when NO championship is active */}
      {iAmQualifier && !currentQ && !roundIntro && (
        <QualifierBanner qualifier={status.qualifier} invite={status.invite} onChange={refreshStatus} />
      )}

      {/* Player HUD — visible if I'm a contender (no lives/shield anymore) */}
      {iAmContender && (
        <div className="nk-card p-4 flex items-center gap-4 border-nk-gold/50" data-testid="my-hud">
          <AvatarIcon id={me?.avatar} size={32} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">You · {me?.username || user?.username}</p>
            <p className="text-[10px] font-mono uppercase text-nk-dim">
              Seed #{me?.seed} · {me?.total_score ?? 0} pts
            </p>
          </div>
        </div>
      )}

      {/* Current question — every contender answers, no eliminations */}
      {currentQ && !currentQ.ended && (
        <div className="nk-card p-5" data-testid="champ-current-question">
          <p className="eyebrow mb-2">Question {currentQ.index + 1} of {currentQ.total}</p>
          <p className="text-lg font-medium mb-4">{currentQ.body}</p>
          {/* Lifelines HUD for contenders */}
          {iAmContender && (
            <LifelinesRow me={me} currentQ={currentQ} onUsed={refreshStatus} />
          )}
          <ul className="space-y-2 mt-3">
            {currentQ.options.map((o, i) => {
              const isMyPick = myPick === i;
              const isHidden = currentQ.fifty_removed?.includes(i);
              const canPick = iAmContender && myPick == null && !submitting && !isHidden;
              const baseCls = "w-full text-left border rounded-sm px-4 py-2 text-sm transition-colors";
              const stateCls = isHidden
                ? "border-nk-line opacity-30 line-through"
                : isMyPick
                ? "border-nk-gold bg-nk-gold/10 text-nk-gold"
                : canPick
                ? "border-nk-line hover:border-nk-gold hover:bg-nk-gold/5 cursor-pointer"
                : "border-nk-line";
              return (
                <li key={i}>
                  {iAmContender ? (
                    <button
                      onClick={() => submitAnswer(i)}
                      disabled={!canPick}
                      data-testid={`answer-opt-${i}`}
                      className={`${baseCls} ${stateCls} disabled:cursor-not-allowed`}
                    >
                      <span className="text-nk-mute mr-2">{String.fromCharCode(65 + i)}.</span> {o}
                    </button>
                  ) : (
                    <div className={`${baseCls} ${stateCls}`}>
                      <span className="text-nk-mute mr-2">{String.fromCharCode(65 + i)}.</span> {o}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          {iAmContender ? (
            myPick != null
              ? <p className="mt-3 text-xs text-nk-gold">Locked in · <b>{String.fromCharCode(65 + myPick)}</b>. Waiting for reveal…</p>
              : <p className="mt-3 text-xs text-nk-mute">Pick fast — fastest correct earns +25, other correct = +10. No eliminations.</p>
          ) : (
            <p className="mt-3 text-xs text-nk-mute italic">
              Spectators can&apos;t submit — watch the leaders answer live.
            </p>
          )}
        </div>
      )}
      {currentQ?.ended && (
        <div className="nk-card p-5 border-l-2 border-nk-good" data-testid="champ-question-revealed">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-nk-good">
              ✓ Correct: <span className="font-medium">{String.fromCharCode(65 + currentQ.correct_index)}. {currentQ.options[currentQ.correct_index]}</span>
            </p>
            <NextQuestionCountdown />
          </div>
          {myPick != null && (
            <p className={`mt-1 text-xs ${myPick === currentQ.correct_index ? "text-nk-good" : "text-nk-bad"}`}>
              You picked {String.fromCharCode(65 + myPick)} · {myPick === currentQ.correct_index ? "correct!" : "wrong"}
            </p>
          )}
        </div>
      )}

      {/* Reactions row */}
      <div className="nk-card p-3 flex items-center gap-2 flex-wrap" data-testid="champ-reactions">
        <span className="text-xs text-nk-mute mr-2 flex items-center gap-1"><ChatCircle size={13} /> React:</span>
        {REACTION_EMOJIS.map((e) => (
          <button
            key={e}
            onClick={() => send(e)}
            data-testid={`reaction-${e}`}
            className="text-xl hover:scale-125 active:scale-95 transition-transform"
          >
            {e}
          </button>
        ))}
      </div>

      {c.state === "ENDED" && c.champion_user_id && (
        <div className="nk-card p-6 text-center border border-nk-gold" data-testid="champ-winner">
          <Crown size={40} weight="fill" className="mx-auto text-nk-gold mb-2" />
          <p className="eyebrow text-nk-gold mb-1">Continental Champion</p>
          <h2 className="font-display text-3xl">
            {c.contenders?.find((x) => x.user_id === c.champion_user_id)?.username}
          </h2>
        </div>
      )}
    </div>
  );
}

function BracketPill({ p, state, flash }) {
  // "state" retained for future admin overrides. In the new system nobody is
  // eliminated by gameplay; only admin force-eliminate hits the `eliminated`
  // flag on the server. If that ever happens we soften-dim rather than strike.
  const isOut = state === "out";
  const flashCls = flash === "correct"
    ? "border-nk-gold shadow-[0_0_10px_rgba(255,193,7,0.5)] animate-pulse"
    : flash === "fastest"
    ? "border-nk-gold shadow-[0_0_16px_rgba(255,193,7,0.8)] animate-pulse"
    : "";
  return (
    <div
      data-testid={`bracket-${state}-${p.user_id}`}
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-xs border transition-colors ${
        isOut
          ? "border-nk-line text-nk-mute opacity-50"
          : `border-nk-gold/60 text-nk-text ${flashCls}`
      }`}
    >
      <AvatarIcon id={p.avatar} size={16} />
      <span>{p.username}</span>
      <CountryFlag country={p.country} size={10} />
      <span className="font-mono text-nk-mute ml-1">{p.total_score}</span>
    </div>
  );
}

/**
 * QualifierBanner — shown to top-5 GH/NG when there's no active championship.
 * Lets the player Accept or Decline the invitation to play Day 31.
 */
function QualifierBanner({ qualifier, invite, onChange }) {
  const country = qualifier.country === "GH" ? "Ghana" : "Nigeria";
  const status = invite?.status || "pending";
  const alreadyResponded = status === "accepted" || status === "declined";

  const respond = async (kind) => {
    if (!invite) return;
    try {
      await api.post(`/championship/invite/${kind}`, { champ_id: invite.champ_id });
      toast.success(kind === "accept" ? "Locked in — see you on Day 31" : "Declined — you can still watch");
      onChange && onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="nk-card p-6 border-nk-gold/60 bg-gradient-to-br from-nk-gold/[0.08] to-transparent"
         data-testid="qualifier-banner">
      <div className="flex items-start gap-4 flex-wrap">
        <Trophy size={40} className="text-nk-gold flex-shrink-0" weight="fill" />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-mono uppercase tracking-widest text-nk-gold mb-2">
            You&apos;ve qualified
          </p>
          <h2 className="font-display text-2xl mb-1">
            Seed #{qualifier.rank} · {country}
          </h2>
          <p className="text-sm text-nk-mute">
            You&apos;re one of {country}&apos;s top 5. Confirm your seat and you&apos;ll play the
            Continental Championship live on Day 31 for a shot at the Trophy + Country Cup.
          </p>
          {invite && (
            <p className="text-[10px] font-mono uppercase text-nk-dim mt-2">
              Status: {status.toUpperCase()}
              {invite.sent_at && ` · Invited ${new Date(invite.sent_at).toLocaleDateString()}`}
            </p>
          )}
        </div>
      </div>

      {invite ? (
        alreadyResponded ? (
          <div className="mt-4 flex items-center gap-3">
            <span className={`text-xs font-mono uppercase px-3 py-1.5 rounded-sm border ${
              status === "accepted"
                ? "border-nk-good/40 bg-nk-good/10 text-nk-good"
                : "border-nk-bad/40 bg-nk-bad/10 text-nk-bad"
            }`}>
              {status === "accepted" ? "✓ Seat confirmed" : "✕ Declined"}
            </span>
            {status === "declined" && (
              <button onClick={() => respond("accept")} data-testid="invite-reconsider"
                      className="text-xs text-nk-gold hover:underline">
                Changed your mind? Accept
              </button>
            )}
          </div>
        ) : (
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <button
              onClick={() => respond("accept")}
              data-testid="invite-accept"
              className="px-4 py-2 rounded-sm bg-nk-gold text-nk-charcoal font-medium text-sm hover:brightness-110 transition-all">
              Accept my seat
            </button>
            <button
              onClick={() => respond("decline")}
              data-testid="invite-decline"
              className="px-4 py-2 rounded-sm border border-nk-line text-sm hover:border-nk-bad hover:text-nk-bad transition-colors">
              Decline
            </button>
          </div>
        )
      ) : (
        <div className="mt-4 p-3 rounded-sm border border-nk-line bg-nk-charcoal/40">
          <p className="text-xs text-nk-mute">
            Invites go out closer to Day 30. Keep your rank in the Top 5 to hold your seat.
          </p>
        </div>
      )}
    </div>
  );
}

function IntroBadge({ Icon, label, value, tone = "default" }) {
  const toneCls = tone === "bad" ? "text-nk-bad" : tone === "info" ? "text-blue-400" : "text-nk-text";
  return (
    <div className="flex items-center gap-2 px-3 py-2 border border-nk-line rounded-sm bg-nk-charcoal/40">
      <Icon size={14} className={toneCls} weight="fill" />
      <div className="min-w-0">
        <p className="text-[9px] font-mono uppercase text-nk-dim leading-none">{label}</p>
        <p className={`text-sm font-mono ${toneCls} truncate`}>{value}</p>
      </div>
    </div>
  );
}


/**
 * NextQuestionCountdown — 8-second countdown shown during the reveal step.
 * Matches backend REVEAL_SECONDS (8). Pure client-side estimate so no server
 * chatter needed.
 */
function NextQuestionCountdown() {
  const [remaining, setRemaining] = useState(8);
  useEffect(() => {
    if (remaining <= 0) return;
    const t = setTimeout(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearTimeout(t);
  }, [remaining]);
  return (
    <div className="flex items-center gap-2" data-testid="next-question-countdown">
      <span className="text-[10px] font-mono uppercase text-nk-dim">Next in</span>
      <span className={`font-mono text-lg ${remaining <= 3 ? "text-nk-gold" : "text-nk-mute"}`}>
        {remaining}s
      </span>
    </div>
  );
}


/**
 * ContenderReveal — animated per-country flip-cards during the welcome
 * countdown. Sorted by country then seed so viewers see NG #1..5 then GH #1..5
 * with a staggered flip-in animation building hype for Q1.
 */
function ContenderReveal({ contenders }) {
  if (!contenders || contenders.length === 0) return null;
  const byCountry = contenders.reduce((acc, c) => {
    (acc[c.country] = acc[c.country] || []).push(c);
    return acc;
  }, {});
  const countries = Object.keys(byCountry).sort();  // deterministic order
  let seq = 0;  // running stagger index across all cards
  return (
    <div className="mt-6" data-testid="contender-reveal">
      <p className="text-[10px] font-mono uppercase text-nk-gold mb-3">The Contenders</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {countries.map((country) => {
          const roster = [...byCountry[country]].sort((a, b) => (a.seed || 99) - (b.seed || 99));
          return (
            <div key={country} className="rounded-md border border-nk-gold/20 bg-nk-charcoal/40 p-3">
              <div className="flex items-center gap-2 mb-2">
                <CountryFlag country={country} size={14} />
                <span className="text-xs font-mono uppercase tracking-widest text-nk-gold/80">
                  {country}
                </span>
                <span className="text-[10px] text-nk-dim font-mono">
                  · {roster.length} contender{roster.length === 1 ? "" : "s"}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {roster.map((c) => {
                  const delay = (seq++) * 0.18; // stagger flip-in per card
                  return (
                    <div
                      key={c.user_id}
                      className="nk-flip-in flex items-center gap-3 px-3 py-2 rounded-sm border border-nk-line bg-black/30"
                      style={{ animationDelay: `${delay}s` }}
                      data-testid={`contender-reveal-${c.user_id}`}
                    >
                      <span className="font-mono text-[10px] text-nk-gold w-6">
                        #{c.seed}
                      </span>
                      <AvatarIcon id={c.avatar} size={26} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{c.username}</p>
                        <p className="text-[10px] font-mono text-nk-dim">
                          KR &middot; {c.season_score || 0}
                        </p>
                      </div>
                      <CountryFlag country={c.country} size={11} />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


/**
 * LifelinesRow — shows how many AI lifelines the contender has left and lets
 * them spend one on the current question. Available types: ai_answer, fifty_fifty.
 * Top-3 seeds start with 2, everyone else with 1.
 */
function LifelinesRow({ me, currentQ, onUsed }) {
  const total = me?.lifelines_total || 0;
  const used = (me?.lifelines_used || []).length;
  const remaining = Math.max(0, total - used);
  const alreadyUsedThisQ = (me?.lifelines_used || []).some(
    (u) => u.round === currentQ.round && u.question_index === currentQ.index
  );
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState(null);   // {type, recommended_index, confidence, reason} or {type, removed}

  const use = async (kind) => {
    if (busy || remaining <= 0 || alreadyUsedThisQ) return;
    setBusy(true);
    try {
      const { data } = await api.post("/championship/lifelines/use", {
        type: kind,
        round: currentQ.round,
        question_index: currentQ.index,
        qid: currentQ.qid,
      });
      setHint(data);
      onUsed && onUsed();
      // If fifty_fifty, splash removed indices into the current question
      // (parent will re-render on my-status refresh, but for immediate UX
      // we mutate a local field so the option list dims right away)
      if (kind === "fifty_fifty" && Array.isArray(data.removed)) {
        // Mutate via prop hack — simple and works
        currentQ.fifty_removed = data.removed;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lifeline failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-3 flex-wrap p-3 rounded-sm border border-nk-line bg-nk-charcoal/40 mb-3"
         data-testid="lifelines-row">
      <span className="text-[10px] font-mono uppercase text-nk-gold">
        Lifelines · {remaining}/{total}
      </span>
      <button
        onClick={() => use("fifty_fifty")}
        disabled={busy || remaining <= 0 || alreadyUsedThisQ}
        data-testid="lifeline-fifty-fifty"
        className="text-xs px-3 py-1.5 border border-nk-line hover:border-nk-gold hover:text-nk-gold rounded-sm disabled:opacity-30 disabled:cursor-not-allowed">
        50 · 50
      </button>
      <button
        onClick={() => use("ai_answer")}
        disabled={busy || remaining <= 0 || alreadyUsedThisQ}
        data-testid="lifeline-ai-answer"
        className="text-xs px-3 py-1.5 border border-nk-line hover:border-nk-gold hover:text-nk-gold rounded-sm disabled:opacity-30 disabled:cursor-not-allowed">
        Ask AI
      </button>
      {alreadyUsedThisQ && (
        <span className="text-[10px] font-mono text-nk-dim">Used this Q</span>
      )}
      {hint?.type === "ai_answer" && (
        <p className="text-xs text-nk-gold ml-auto">
          AI suggests <b>{String.fromCharCode(65 + hint.recommended_index)}</b>
          {" "}({Math.round(hint.confidence * 100)}% conf)
        </p>
      )}
      {hint?.type === "fifty_fifty" && (
        <p className="text-xs text-nk-mute ml-auto">50·50 — {hint.removed.length} options removed</p>
      )}
    </div>
  );
}

/**
 * PreStartLobby — shown when a champ is scheduled but not yet running.
 * Displays kickoff countdown, accepted-players grid, rules overview, and
 * a "Watch when it starts" reassurance for non-contenders.
 */
function PreStartLobby({ lobby, status }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const scheduledMs = new Date(lobby.scheduled_at).getTime();
  const remaining = Math.max(0, Math.floor((scheduledMs - now) / 1000));
  const d = Math.floor(remaining / 86400);
  const h = Math.floor((remaining % 86400) / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  const s = remaining % 60;
  const role = status?.role;
  const ng = lobby.accepted.filter(a => a.country === "NG");
  const gh = lobby.accepted.filter(a => a.country === "GH");

  return (
    <div className="space-y-4" data-testid="prestart-lobby">
      <div className="nk-card p-6 border-nk-gold/60 bg-gradient-to-br from-nk-gold/[0.06] to-transparent">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-nk-gold mb-2">
              Continental Championship · Season {lobby.season_number}
            </p>
            <h1 className="font-display text-3xl">
              {remaining > 0 ? "Arena opens in" : "Kickoff imminent — waiting for admin to launch"}
            </h1>
            {remaining > 0 && (
              <div className="mt-3 flex items-center gap-4 flex-wrap font-mono">
                <Slot n={d} label="days" />
                <Slot n={h} label="hrs" />
                <Slot n={m} label="min" />
                <Slot n={s} label="sec" />
              </div>
            )}
            <p className="text-xs text-nk-mute mt-3">
              {new Date(scheduledMs).toLocaleString(undefined, {
                weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit",
              })}
            </p>
          </div>
          {role === "contender" && (
            <div className="p-3 rounded-sm border border-nk-gold/40 bg-nk-charcoal/50">
              <p className="text-xs text-nk-gold">✓ You&apos;re playing · Seed #{status.contender?.seed}</p>
              <p className="text-[10px] text-nk-mute mt-1">
                {(status.contender?.lifelines_total || 1)} AI lifeline{status.contender?.lifelines_total === 2 ? "s" : ""} available
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="nk-card p-5">
        <p className="eyebrow mb-3">How it works</p>
        <ul className="space-y-2 text-sm text-nk-text">
          <li className="flex gap-2"><span className="text-nk-gold">•</span> 4 themed rounds · 5 questions each · 20s per question</li>
          <li className="flex gap-2"><span className="text-nk-gold">•</span> No elimination — everyone plays every question. Highest total score wins.</li>
          <li className="flex gap-2"><span className="text-nk-gold">•</span> Top 3 seeds get <b className="text-nk-gold">2 AI lifelines</b>, everyone else gets <b className="text-nk-gold">1</b>. Choose &apos;Ask AI&apos; or &apos;50·50&apos;.</li>
          <li className="flex gap-2"><span className="text-nk-gold">•</span> Fastest-correct earns +25 bonus points. Standard correct = +10.</li>
          <li className="flex gap-2"><span className="text-nk-gold">•</span> Round 2 also decides the Country Cup — highest country total wins bonus NK+ for its citizens.</li>
        </ul>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <LobbyColumn country="NG" label="Nigeria" players={ng} />
        <LobbyColumn country="GH" label="Ghana" players={gh} />
      </div>

      {lobby.pending_count > 0 && (
        <p className="text-xs text-nk-mute text-center font-mono">
          {lobby.pending_count} qualifier{lobby.pending_count === 1 ? "" : "s"} yet to confirm ·
          {" "}{lobby.declined_count} declined
        </p>
      )}
    </div>
  );
}

function Slot({ n, label }) {
  return (
    <div className="text-center">
      <p className="text-3xl md:text-5xl text-nk-gold font-mono tabular-nums">{String(n).padStart(2, "0")}</p>
      <p className="text-[10px] uppercase text-nk-dim">{label}</p>
    </div>
  );
}

function LobbyColumn({ country, label, players }) {
  return (
    <div className="nk-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <CountryFlag country={country} size={14} />
        <p className="font-display text-lg">{label}</p>
        <span className="text-xs text-nk-mute font-mono ml-auto">{players.length} confirmed</span>
      </div>
      <div className="space-y-1.5">
        {players.length === 0 ? (
          <p className="text-xs text-nk-dim py-3 text-center">No confirmed seats yet</p>
        ) : players.map((p) => (
          <div key={p.user_id} className="flex items-center gap-2 px-2 py-1.5 border border-nk-line rounded-sm">
            <span className="text-nk-gold font-mono text-xs w-6">#{p.seed}</span>
            <AvatarIcon id={p.avatar} size={20} />
            <span className="text-sm truncate flex-1">{p.username}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

